import { Badge } from '@/components/ui/badge'
import { displayDate, displayDateTime } from '@/lib/dates'
import type { EngagementListKey } from './engagementListState'

export type EngagementRow = Record<string, string | number | null>
export interface EngagementColumn {
  key: string
  label: string
  /** Absent for a column the server cannot sort by. */
  sort?: string
  right?: boolean
  cell: (row: EngagementRow) => React.ReactNode
}
export const categoryLabels: Record<string, string> = {
  membership: 'Membership',
  events: 'Events',
  training: 'Training',
  sponsorship: 'Sponsorship',
  donations: 'Donations',
  other: 'Other',
}
const statusLabels: Record<string, string> = {
  enrolled: 'In progress',
  completed: 'Completed',
  failed: 'Failed',
  withdrawn: 'Withdrawn',
  active: 'Active',
}
const statusVariants = {
  enrolled: 'info',
  completed: 'success',
  failed: 'destructive',
  withdrawn: 'neutral',
  active: 'success',
} as const
function person(row: EngagementRow) {
  return (
    <>
      <span className="font-medium">
        {row.person_id ? (
          <a className="hover:underline" href={`/contacts/persons/${row.person_id}`}>
            {row.person_name}
          </a>
        ) : (
          row.person_name
        )}
      </span>
      {row.person_status === 'former' && (
        <Badge variant="neutral" className="ml-1.5">
          Former
        </Badge>
      )}
    </>
  )
}
function status(row: EngagementRow) {
  const key = String(row.status) as keyof typeof statusVariants
  return <Badge variant={statusVariants[key]}>{statusLabels[key] ?? key}</Badge>
}
const text = (key: string) => (row: EngagementRow) => row[key]
const date = (key: string) => (row: EngagementRow) =>
  row[key] ? displayDate(String(row[key]).slice(0, 10)) : '—'
const personColumn = { key: 'person', label: 'Person', sort: 'person', cell: person }
export const listColumns: Record<EngagementListKey, EngagementColumn[]> = {
  courses_completed: [
    personColumn,
    { key: 'course', label: 'Course', sort: 'course', cell: text('course_title') },
    { key: 'status', label: 'Status', sort: 'status', cell: status },
    {
      key: 'completed_on',
      label: 'Completed on',
      sort: 'completed_on',
      cell: date('completed_on'),
    },
    { key: 'credits', label: 'Credits', sort: 'credits', right: true, cell: text('credits') },
  ],
  email_clicks: [
    personColumn,
    {
      key: 'emails_sent',
      label: 'Emails sent',
      sort: 'emails_sent',
      right: true,
      cell: text('emails_sent'),
    },
    { key: 'clicks', label: 'Clicks', sort: 'clicks', right: true, cell: text('clicks') },
    {
      key: 'last_click_at',
      label: 'Last click',
      sort: 'last_click_at',
      cell: (row) => displayDateTime(row.last_click_at ? String(row.last_click_at) : null),
    },
  ],
  personal_memberships: [
    personColumn,
    { key: 'level', label: 'Level', sort: 'level', cell: text('level_name') },
    { key: 'status', label: 'Status', sort: 'status', cell: status },
    {
      key: 'renewal_due_on',
      label: 'Renewal due',
      sort: 'renewal_due_on',
      cell: date('renewal_due_on'),
    },
  ],
  spend: [
    {
      key: 'number',
      label: 'Invoice number',
      sort: 'number',
      cell: (row) => (
        <a
          className="font-medium text-primary hover:underline"
          href={`/finance/invoices/${row.id}`}
        >
          {row.number}
        </a>
      ),
    },
    {
      key: 'billed_to',
      label: 'Billed to',
      sort: 'billed_to',
      cell: (row) => (
        <>
          {row.billed_to}
          {row.person_status === 'former' && (
            <Badge variant="neutral" className="ml-1.5">
              Former
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sort: 'category',
      cell: (row) => (
        <>
          {categoryLabels[String(row.category)]}
          <SpendLinksCaption row={row} />
        </>
      ),
    },
    { key: 'issue_on', label: 'Issue date', sort: 'issue_on', cell: date('issue_on') },
    { key: 'net', label: 'Net', sort: 'net', right: true, cell: text('net') },
    { key: 'unpaid', label: 'Unpaid', sort: 'unpaid', right: true, cell: text('unpaid') },
  ],
}
export const listDefinitions = {
  courses_completed: {
    title: 'Courses completed',
    search: 'Search person or course',
    sort: 'completed_on_desc',
  },
  email_clicks: { title: 'Clicked an email', search: 'Search person', sort: 'clicks_desc' },
  personal_memberships: {
    title: 'Personal memberships',
    search: 'Search person',
    sort: 'person_asc',
  },
  spend: {
    title: 'Spend',
    search: 'Search invoice, person, course or event',
    sort: 'issue_on_desc',
  },
}

interface SpendLinks {
  event: { id: number; name: string } | null
  purchaser: {
    id: number | null
    display_name: string
    membership_level_name: string | null
  } | null
  course: { id: number; name: string } | null
}
const spendLinks = (row: EngagementRow) => row as unknown as Partial<SpendLinks>
function EventLink({ event }: { event: NonNullable<SpendLinks['event']> }) {
  return (
    <a className="text-primary hover:underline" href={`/events/manage/${event.id}`}>
      {event.name}
    </a>
  )
}
function PurchaserLink({ purchaser }: { purchaser: NonNullable<SpendLinks['purchaser']> }) {
  return purchaser.id ? (
    <a className="text-primary hover:underline" href={`/contacts/persons/${purchaser.id}`}>
      {purchaser.display_name}
    </a>
  ) : (
    <>{purchaser.display_name}</>
  )
}
function SpendLinksCaption({ row }: { row: EngagementRow }) {
  const { event, purchaser, course } = spendLinks(row)
  if (!event && !purchaser && !course) return null
  return (
    <span className="block text-caption text-muted-foreground">
      {event && <EventLink event={event} />}
      {course?.name}
      {purchaser && (
        <>
          {course ? ' · for ' : 'For '}
          <PurchaserLink purchaser={purchaser} />
        </>
      )}
    </span>
  )
}
const [numberColumn, billedToColumn, , ...trailingColumns] = listColumns.spend
const eventColumn: EngagementColumn = {
  key: 'event',
  label: 'Event',
  cell: (row) => {
    const { event } = spendLinks(row)
    return event ? <EventLink event={event} /> : '—'
  },
}
const purchasedByColumn: EngagementColumn = {
  key: 'purchased_by',
  label: 'Purchased by',
  cell: (row) => {
    const { purchaser } = spendLinks(row)
    if (!purchaser) return billedToColumn.cell(row)
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <PurchaserLink purchaser={purchaser} />
        {purchaser.membership_level_name && (
          <Badge variant="neutral">{purchaser.membership_level_name}</Badge>
        )}
      </span>
    )
  },
}
const programmeColumn: EngagementColumn = {
  key: 'training_programme',
  label: 'Training programme',
  cell: (row) => spendLinks(row).course?.name ?? categoryLabels.training,
}
/** Spend columns for the selected category: sponsorship names its event, training its buyer and course. */
export function spendColumns(category: string | null): EngagementColumn[] {
  if (category === 'sponsorship')
    return [numberColumn, billedToColumn, eventColumn, ...trailingColumns]
  if (category === 'training')
    return [numberColumn, purchasedByColumn, programmeColumn, ...trailingColumns]
  return listColumns.spend
}
export function columnsFor(tile: EngagementListKey, params: URLSearchParams) {
  return tile === 'spend' ? spendColumns(params.get('filters[category]')) : listColumns[tile]
}
